import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { FilterService } from '@app/core/services/container/filter.service';
import { SidenavService } from '@app/core/services/container/sidenav.service';
import { DivisionContextService } from '@app/core/services/division/division-context.service';
import { SessionService } from '@app/core/services/session/session.service';
import { UserService } from '@app/core/services/user/user.service';
import { User } from '@app/shared/models/user.interface';
import { CustomOverlayService } from '@app/shared/services/overlay/custom-overlay.service';
import { EntitySelectionModel } from '@components/data/data-table-new/entitySelectionModel';
import { DataTableState } from '@components/data/data-table-new/state/state.model';
import { StateService } from '@components/data/data-table-new/state/state.service';
import { combineLatest, Observable, of } from 'rxjs';
import { startWith, tap } from 'rxjs/operators';
import { UserListOverlayComponent } from './user-list-overlay/user-list-overlay.component';
import { UserListWidgetTableData } from './user-list-widget.component.table';

@Component({
  selector: 'vfs-user-list-widget',
  templateUrl: './user-list-widget.component.html',
  styleUrls: ['./user-list-widget.component.scss'],
  providers: [CustomOverlayService]
})
export class UserListWidgetComponent implements OnInit, OnDestroy {
  tableState$: Observable<DataTableState>;
  globalDivisionFilterTrigger$: Observable<any>;
  all$: Observable<any>;
  _users: User[] = [];
  initialSelection$: Observable<User[]>;
  @Input() initialSelection: string[];
  @Input() disabled = false;
  @Input() disableInitialSelection = false;
  @Output() selected = new EventEmitter<User[]>();

  userDivisionsChecked: { [key: string]: boolean } = {};

  constructor(
    @Inject('StateService') protected stateService: StateService,
    protected divisionContextService: DivisionContextService,
    public userListWidgetTableData: UserListWidgetTableData,
    protected userService: UserService,
    protected filterService: FilterService,
    protected sidenavService: SidenavService,
    protected sessionService: SessionService,
    public customOverlayService: CustomOverlayService
  ) {}

  ngOnInit() {
    if (this.initialSelection && this.initialSelection.length > 0) {
      this.initialSelection$ = this.userService
        .getByIds(this.initialSelection)
        .pipe(tap(users => (this.users = users)));
    } else {
      this.initialSelection$ = of([]);
    }

    this.getState().filter.search = '';
    this.tableState$ = this.userListWidgetTableData.stateSubject$.pipe(
      startWith(this.getState()),
      tap(newState => {
        this.refreshData(newState);
      })
    );
    this.globalDivisionFilterTrigger$ = this.divisionContextService.divisionFilter$
      .asObservable()
      .pipe(tap(_ => this.refreshData(this.getState())));
    this.all$ = combineLatest([this.tableState$, this.globalDivisionFilterTrigger$]);
  }

  ngOnDestroy(): void {
    this.deleteState();
  }

  masterToggle() {
    this.checkIfDataIsSelected(this.userListWidgetTableData.selection, this.userListWidgetTableData.datasource.data)
      ? this.userListWidgetTableData.selection.clear()
      : this.userListWidgetTableData.datasource.data.forEach((row: User) => {
          if (this.userDivisionsChecked[row.id]) {
            this.userListWidgetTableData.selection.select(row);
          }
        });
  }

  checkIfDataIsSelected(selection: EntitySelectionModel<User>, data: User[]): boolean {
    return data.some(user => selection.isSelected(user));
  }

  getCheckedUserDivisions(users: User[]): void {
    users.forEach((user: User) => {
      this.userDivisionsChecked[user.id] = this.checkUserDivisions(user);
    });
  }

  refreshData(state: DataTableState) {
    this.userListWidgetTableData.datasource.loading = true;
    let usersSearch = this.searchUsers(this.users, state);
    this.sortUsers(usersSearch, state);
    this.userListWidgetTableData.datasource.setData(usersSearch);
    this.getCheckedUserDivisions(this.userListWidgetTableData.datasource.data);
    this.userListWidgetTableData.datasource.loading = false;
  }

  protected sortUsers(users: User[], state: DataTableState) {
    users.sort((a, b) => {
      if (a[state.sort.column] < b[state.sort.column]) {
        return state.sort.direction === 'asc' ? -1 : 1;
      } else if (a[state.sort.column] > b[state.sort.column]) {
        return state.sort.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  protected searchUsers(users: User[], state: DataTableState) {
    return users.filter(user => this.filterUser(user, state.filter.search));
  }

  protected filterUser(user: User, search: string) {
    let searchValue = this.normalize(search);
    return (
      this.normalize(user.name).includes(searchValue) ||
      this.normalize(user.surname).includes(searchValue) ||
      this.normalize(user.profile.name).includes(searchValue) ||
      this.normalize(this.getDivisions(user)).includes(searchValue) ||
      this.normalize(user.phone).includes(searchValue) ||
      this.normalize(user.email).includes(searchValue)
    );
  }

  protected getState() {
    return this.stateService.get(this.userListWidgetTableData.stateId);
  }

  protected deleteState(): void {
    this.stateService.delete(this.userListWidgetTableData.stateId);
  }

  public getDivisions(user: User): string {
    return user.userDivisions.map(userDivision => userDivision.division.description).join(', ');
  }

  public select(template: TemplateRef<UserListOverlayComponent>) {
    this.sidenavService.openSidenav(template, true);
  }

  get users(): User[] {
    return this._users;
  }

  set users(users: User[]) {
    if (users) {
      this._users = users;
      this.refreshData(this.getState());
    }
    this.closeSidenav();
  }

  public remove(user: User) {
    this.users.splice(this.users.indexOf(user), 1);
    this.refreshData(this.getState());
    this.selected.emit(this.users);
  }

  public removeSelection() {
    this.userListWidgetTableData.selection.selected.forEach(user => {
      this.users.splice(this.users.indexOf(user), 1);
    });
    this.userListWidgetTableData.selection.clear();
    this.refreshData(this.getState());
  }

  checkUserDivisions(user: User): boolean {
    const divisions: string[] = this.sessionService
      .getUser()
      .userDivisions.map(ud => (ud.division.active ? ud.division.id : null))
      .filter(d => d !== null);
    const userDivisions: string[] = user.userDivisions
      .map(ud => (ud.division.active ? ud.division.id : null))
      .filter(d => d !== null);
    return divisions.some(div => userDivisions.includes(div));
  }

  protected closeSidenav() {
    this.sidenavService.closeSidenav();
  }

  protected normalize(text: string): string {
    return text
      ? text
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
      : '';
  }

  selectionToggle(row: any): void {
    if (this.userDivisionsChecked[row.id]) {
      this.userListWidgetTableData.selection.toggle(row);
    }
  }
}
